import React, { forwardRef, useState } from "react";
import { buildJobSelectionTablePages } from "../utils/templatePdfExport";
import sunnyHomesLogo from "assets/images/SH-logo1.png";

// A4 portrait page @ 96dpi — see templatePdfExport.js's PAGE_WIDTH_PX/PAGE_HEIGHT_PX, which must
// stay in sync with these for the pagination math to line up exactly.
const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;
const BORDER_COLOR = "#333333";

// Fields rendered before the item's Dynamic Fields (in order), then Comments/Locked Date after.
// Type (Level 3) isn't in this list — it's promoted to its own centered heading above the option
// box instead (see TableSection below), so it isn't repeated as a row inside the box too.
// Dynamic Fields aren't in this list either — item.dynamicFields is a variable-length array of
// {label, value} (see buildDynamicFieldRows in templatePdfExport.js), so it's spliced in between
// by buildVisibleRows below rather than being a fixed FIELD_ROWS entry.
// "Brand"/"Range" here are only the last-resort fallback label — buildVisibleRows prefers the
// item's own brandLabel/rangeLabel, which templatePdfExport.js's resolveLevelAliasesByMainCategory
// resolves per item from that item's actual Main Category's configured Level 4/5 alias (the same
// one Selection Management's "Edit Selection Option" shows), so a renamed level (e.g. "Level 4" or
// a custom name) is labelled consistently here instead of always printing the legacy term.
const FIELD_ROWS_BEFORE_DYNAMIC = [
  { key: "option", label: "Name" },
  { key: "brand", label: "Brand", labelKey: "brandLabel" },
  { key: "range", label: "Range", labelKey: "rangeLabel" },
  { key: "locations", label: "Locations" },
];
// Builds the ordered, already-filtered list of {key, label, value, isLockedDate} rows for one
// item: Name is always first, then present static fields, then dynamic fields, then Comments
// (always shown, even blank), then Locked Date (only when present).
function buildVisibleRows(item) {
  const rows = [
    { key: "option", label: "Name", value: item.option || "" },
  ];

  FIELD_ROWS_BEFORE_DYNAMIC.slice(1).forEach((row) => {
    if (hasValue(item[row.key])) {
      const label = (row.labelKey && item[row.labelKey]) || row.label;
      rows.push({ key: row.key, label, value: item[row.key] });
    }
  });

  (item.dynamicFields || []).forEach((field, idx) => {
    if (hasValue(field.value)) {
      rows.push({ key: `dyn-${idx}`, label: field.label, value: field.value });
    }
  });

  rows.push({ key: "comments", label: "Comments", value: item.comments || "" });

  if (hasValue(item.lockedDate)) {
    rows.push({ key: "lockedDate", label: "Locked Date", value: item.lockedDate, isLockedDate: true });
  }

  return rows;
}

const ROW_HEIGHT = 22;

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

function PdfTableImage({ src, alt }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
        }}
      />
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
        boxSizing: "border-box",
        background: "#ffffff",
      }}
    >
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        style={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          background: "#ffffff",
        }}
      />
    </div>
  );
}

function FieldRow({ label, value, isLast }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        flex: 1,
        minHeight: ROW_HEIGHT,
        boxSizing: "border-box",
        borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}`,
        lineHeight: 1.25,
      }}
    >
      <div
        style={{
          width: 95,
          flexShrink: 0,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          padding: "2px 8px",
          fontSize: 10,
          fontWeight: 700,
          borderRight: `1px solid ${BORDER_COLOR}`,
          background: "#ffffff",
          color: "#1e1e1e",
        }}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          padding: "2px 8px",
          fontSize: 10,
          color: "#1e1e1e",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{ 
            width: "100%",
            wordBreak: "break-word"
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// One confirmed option: rendered matching the reference selection-sheet format.
// The Level 3 subcategory (e.g. "Floor", "Walls", "Main Floor") is displayed as the top grey
// bar (no top/side borders, just background color) across the width of the table.
function OptionRow({ item }) {
  const visibleRows = buildVisibleRows(item);
  const subCategoryName = item.type || "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 12,
        boxSizing: "border-box",
      }}
    >
      {/* Level 3 Subcategory Top Grey Bar (no top/side borders, just background color) */}
      {hasValue(subCategoryName) && (
        <div
          style={{
            background: "#f2f2f2",
            textAlign: "center",
            padding: "4px 8px",
            fontSize: 11.5,
            fontWeight: 700,
            color: "#1e1e1e",
            boxSizing: "border-box",
            lineHeight: 1.25,
            border: "none",
          }}
        >
          {subCategoryName}
        </div>
      )}

      {/* Table Box: borders are ONLY around the table rows and photo! */}
      <div
        style={{
          display: "flex",
          border: `1px solid ${BORDER_COLOR}`,
          boxSizing: "border-box",
          minHeight: ROW_HEIGHT * Math.max(visibleRows.length, 4),
        }}
      >
        <div style={{ flex: 1, display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
          {visibleRows.map((row, idx) => (
            <FieldRow
              key={row.key}
              label={row.label}
              value={
                row.isLockedDate ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <span>{row.value}</span>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 6px", borderRadius: 9999, border: "1px solid #10b981", color: "#10b981", fontSize: 9.5, fontWeight: 600 }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                      Locked
                    </div>
                  </div>
                ) : (
                  row.value
                )
              }
              isLast={idx === visibleRows.length - 1}
            />
          ))}
        </div>
        <div
          style={{
            width: 190,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            borderLeft: `1px solid ${BORDER_COLOR}`,
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <PdfTableImage src={item.photo} alt={item.option} />
        </div>
      </div>
    </div>
  );
}

// One Category (e.g. "Ensuite") section: an underlined heading above its option(s).
// When Category (Level 2) is skipped/absent, no category heading is rendered, and the option's
// own Level 3 grey header bar (e.g. "Main Floor") sits directly on the table.
function TableSection({ category, items, showCounts }) {
  const showCategoryTitle = category && category !== "Uncategorised" && category !== "Direct Options";

  return (
    <div style={{ marginBottom: showCategoryTitle ? 12 : 0 }}>
      {showCategoryTitle && (
        <h3
          style={{
            textAlign: "left",
            fontSize: 14,
            fontWeight: 700,
            textDecoration: "underline",
            margin: "4px 0 8px",
            color: "#1e1e1e",
          }}
        >
          {category}
        </h3>
      )}
      {items.map((item) => (
        <OptionRow key={item.id} item={item} />
      ))}
    </div>
  );
}

// Placeholder fill-in line shown when a sign-off field has no value yet (an unsigned template
// export, e.g. SelectionsPage.jsx's plain "Export" button) — matches the "Date:_______" /
// "Client Initials:_______" paper-form convention rather than leaving the field blank.
const SIGN_OFF_BLANK = "_______________";

function Footer({ pageNum, isLastPage, isLockedPdf, signatureDataUrl, approvalDateText }) {
  // Show sign-off footer whenever signature or approval date text is present, or on non-locked PDFs.
  const showSignOff = !isLockedPdf || !!signatureDataUrl || !!approvalDateText;
  return (
    <div
      style={{
        position: "absolute",
        bottom: 36,
        left: 56,
        right: 56,
        fontSize: 12,
        color: "#64748b",
      }}
    >
      {showSignOff && (
        <div style={{ position: "absolute", bottom: 40, left: 0, right: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", height: 32, fontSize: 12, color: "#1e1e1e" }}>
            <span style={{ fontWeight: 700, marginRight: 6, display: "inline-flex", alignItems: "center", height: "100%", lineHeight: 1 }}>Date:</span>
            <span style={{ display: "inline-flex", alignItems: "center", height: "100%", lineHeight: 1 }}>{approvalDateText || SIGN_OFF_BLANK}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", height: 32, fontSize: 12, color: "#1e1e1e" }}>
            <span style={{ fontWeight: 700, marginRight: 6, display: "inline-flex", alignItems: "center", height: "100%", lineHeight: 1 }}>Client Initials:</span>
            {signatureDataUrl ? (
              <img
                src={signatureDataUrl}
                alt="Client signature"
                style={{ display: "inline-block", height: 28, maxWidth: 140, objectFit: "contain", verticalAlign: "middle" }}
              />
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", height: "100%", lineHeight: 1 }}>{SIGN_OFF_BLANK}</span>
            )}
          </div>
        </div>
      )}
      {isLastPage && (
        <div style={{ position: "absolute", left: "50%", bottom: 0, transform: "translateX(-50%)", color: "#94a3b8" }}>
          Sunny Homes ACT
        </div>
      )}
      <div style={{ position: "absolute", right: 0, bottom: 0 }}>{pageNum}</div>
    </div>
  );
}

function TablePage({ jobName, categoryType, sections, pageNum, isLastPage, isLockedPdf, signatureDataUrl, approvalDateText, showCounts }) {
  return (
    <div
      className="pdf-page"
      style={{
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        position: "relative",
        background: "#ffffff",
        fontFamily: "Arial, Helvetica, sans-serif",
        padding: "36px 56px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#1e1e1e" }}>{jobName}</div>
        <img src={sunnyHomesLogo} alt="Sunny Homes" style={{ width: 150 }} />
      </div>
      <h2
        style={{
          textAlign: "center",
          fontSize: 18,
          fontWeight: 700,
          textDecoration: "underline",
          margin: "14px 0 16px",
          color: "#1e1e1e",
        }}
      >
        {categoryType}
      </h2>
      {sections.map((section) => (
        <TableSection key={section.category} category={section.category} items={section.items} showCounts={showCounts} />
      ))}
      <Footer pageNum={pageNum} isLastPage={isLastPage} isLockedPdf={isLockedPdf} signatureDataUrl={signatureDataUrl} approvalDateText={approvalDateText} />
    </div>
  );
}

function EmptyTablePage({ jobName, pageNum, isLastPage, isLockedPdf }) {
  return (
    <div
      className="pdf-page"
      style={{
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        position: "relative",
        background: "#ffffff",
        fontFamily: "Arial, Helvetica, sans-serif",
        padding: "36px 56px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#1e1e1e" }}>{jobName}</div>
        <img src={sunnyHomesLogo} alt="Sunny Homes" style={{ width: 150 }} />
      </div>
      <p style={{ fontSize: 14, color: "#94a3b8", marginTop: 60, textAlign: "center" }}>
        No confirmed selections to show yet.
      </p>
      <Footer pageNum={pageNum} isLastPage={isLastPage} isLockedPdf={isLockedPdf} />
    </div>
  );
}

/**
 * Off-screen printable layout rasterized by templatePdfExport.js's exportJobSelectionsTableToPdf,
 * styled as the company's printed selection-sheet reference format — a portrait A4 page per
 * chunk of Category sections, headed by the job name + Sunny Homes logo and a
 * "{CategoryType} Selection" title. Every .pdf-page below is authored to the exact
 * PAGE_WIDTH x PAGE_HEIGHT pixel size so the pagination math in templatePdfExport.js can slice the
 * rasterized canvas at exact multiples of one page's height, with no boundary-detection heuristics
 * needed (same convention as TemplatePdfPrintLayout's .pdf-slide).
 */
export const JobSelectionTablePrintLayout = forwardRef(({ data }, ref) => {
  if (!data) {
    return <div ref={ref} />;
  }

  const pages = buildJobSelectionTablePages(data.categoryTypes);

  return (
    <div ref={ref} style={{ width: PAGE_WIDTH }}>
      {pages.length > 0
        ? pages.map((page, idx) => (
            <TablePage
              key={idx}
              jobName={data.name}
              categoryType={page.categoryType}
              sections={page.sections}
              pageNum={idx + 1}
              isLastPage={idx === pages.length - 1}
              isLockedPdf={data.isLockedPdf}
              signatureDataUrl={data.signatureDataUrl}
              approvalDateText={data.approvalDateText}
              showCounts={data.showCounts}
            />
          ))
        : <EmptyTablePage jobName={data.name} pageNum={1} isLastPage={true} isLockedPdf={data.isLockedPdf} />}
    </div>
  );
});

JobSelectionTablePrintLayout.displayName = "JobSelectionTablePrintLayout";
